import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchCategories } from '../services/categoriesService';
import { fetchProducts, searchContentItems } from '../services/productsService';
import './UrunCokluSecimModal.css';

const getName = (item) =>
  item?.icerikAdi || item?.urunAdi || item?.parcaAdi || item?.ad || '—';
const getCode = (item) =>
  item?.icerikKodu || item?.urunKodu || item?.parcaKodu || item?.kod || '—';

const mergeCategoriesDedup = (prev, chunk) => {
  const map = new Map();
  (prev || []).forEach((c) => {
    const id = c.id || c._id;
    if (id != null) map.set(String(id), c);
  });
  (chunk || []).forEach((c) => {
    const id = c.id || c._id;
    if (id != null) map.set(String(id), c);
  });
  return Array.from(map.values());
};

/**
 * Parça / arıza formu: çoklu ürün seçimi.
 * onSelectionChange verilirse seçimler anında iletilir; aksi halde 2 adım (seçim + onay).
 */
const UrunCokluSecimModal = ({
  isOpen,
  onClose,
  onConfirm,
  onSelectionChange,
  initialSelected = [],
  aktifFilter,
  stepTitle = 'Ürün seç',
  saving = false,
}) => {
  const instantApply = typeof onSelectionChange === 'function';
  const [step, setStep] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchHits, setSearchHits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [leafProductsByCat, setLeafProductsByCat] = useState({});
  const [leafLoading, setLeafLoading] = useState(() => new Set());
  const [fetchedChildren, setFetchedChildren] = useState(() => new Set());
  const [confirmedLeaf, setConfirmedLeaf] = useState(() => new Set());
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [catBulkLoading, setCatBulkLoading] = useState(() => new Set());
  const skipNextSelectionEmitRef = useRef(false);
  const wasOpenRef = useRef(false);

  const emitSelectionChange = useCallback(
    (nextMap) => {
      if (!onSelectionChange) return;
      onSelectionChange(Array.from(nextMap.values()));
    },
    [onSelectionChange],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 320);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    setStep(1);
    setSearchInput('');
    setDebouncedQ('');
    setSearchHits([]);
    skipNextSelectionEmitRef.current = true;
    const m = new Map();
    (initialSelected || []).forEach((row) => {
      const id = String(row.id || row._id || '');
      if (!id) return;
      m.set(id, {
        id,
        ad: row.ad || getName(row),
        kod: row.kod || getCode(row),
      });
    });
    setSelectedMap(m);
    setExpandedIds(new Set());
    setLeafProductsByCat({});
    setFetchedChildren(new Set());
    setConfirmedLeaf(new Set());
  }, [isOpen, initialSelected]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchCategories({ kategoriTipi: 'urun', ustKategoriId: 'root' });
        const list = res?.veri?.kategoriler || res?.veri || [];
        if (!cancelled) setCategories(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || debouncedQ.length < 3) {
      setSearchHits([]);
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    (async () => {
      try {
        const res = await searchContentItems(debouncedQ, {
          icerikTipi: 'urun',
          aktif: aktifFilter,
          sayfa: 1,
          limit: 100,
        });
        if (cancelled) return;
        const veri = res?.veri || {};
        const list =
          veri.urunler ||
          veri.icerikler ||
          [];
        setSearchHits(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setSearchHits([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedQ, aktifFilter]);

  const updateSelectedMap = useCallback(
    (updater) => {
      setSelectedMap((prev) => {
        const next = updater(prev);
        if (next === prev) return prev;
        if (skipNextSelectionEmitRef.current) {
          skipNextSelectionEmitRef.current = false;
        } else {
          emitSelectionChange(next);
        }
        return next;
      });
    },
    [emitSelectionChange],
  );

  const removeOne = useCallback(
    (idStr) => {
      updateSelectedMap((prev) => {
        const next = new Map(prev);
        next.delete(String(idStr));
        return next;
      });
    },
    [updateSelectedMap],
  );

  const toggleOne = useCallback(
    (item) => {
      const id = item?.id || item?._id;
      if (!id) return;
      const idStr = String(id);
      updateSelectedMap((prev) => {
        const next = new Map(prev);
        if (next.has(idStr)) {
          next.delete(idStr);
        } else {
          next.set(idStr, { id: idStr, ad: getName(item), kod: getCode(item) });
        }
        return next;
      });
    },
    [updateSelectedMap],
  );

  const addCategoryProducts = useCallback(
    async (categoryIdStr) => {
      setCatBulkLoading((prev) => new Set(prev).add(categoryIdStr));
      try {
        let page = 1;
        const allProducts = [];
        let totalPages = 1;
        do {
          // eslint-disable-next-line no-await-in-loop
          const pr = await fetchProducts({
            icerikTipi: 'urun',
            aktif: aktifFilter,
            kategoriId: categoryIdStr,
            altKategorilerDahil: true,
            sayfa: page,
            limit: 100,
          });
          const veri = pr?.veri || {};
          const chunk = veri.icerikler || veri.urunler || [];
          if (Array.isArray(chunk)) allProducts.push(...chunk);
          totalPages = Number(veri.toplamSayfa) || 1;
          page += 1;
        } while (page <= totalPages && page <= 50);
        updateSelectedMap((prev) => {
          const next = new Map(prev);
          allProducts.forEach((p) => {
            const id = String(p.id || p._id);
            if (id && !next.has(id)) {
              next.set(id, { id, ad: getName(p), kod: getCode(p) });
            }
          });
          return next;
        });
      } finally {
        setCatBulkLoading((prev) => {
          const x = new Set(prev);
          x.delete(categoryIdStr);
          return x;
        });
      }
    },
    [aktifFilter, updateSelectedMap],
  );

  const buildTree = useMemo(() => {
    const byParent = new Map();
    categories.forEach((c) => {
      const p = c.ustKategoriId == null || c.ustKategoriId === '' ? 'root' : String(c.ustKategoriId);
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(c);
    });
    const walk = (pid) => {
      const kids = byParent.get(pid) || [];
      return kids.map((c) => ({
        ...c,
        children: walk(String(c.id || c._id)),
      }));
    };
    return walk('root');
  }, [categories]);

  const toggleExpand = async (catIdStr, hasChildrenInTree, isConfirmedLeafCat) => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      if (n.has(catIdStr)) n.delete(catIdStr);
      else n.add(catIdStr);
      return n;
    });

    if (hasChildrenInTree) return;

    if (!fetchedChildren.has(catIdStr)) {
      setFetchedChildren((prev) => new Set(prev).add(catIdStr));
      try {
        const res = await fetchCategories({ kategoriTipi: 'urun', ustKategoriId: catIdStr });
        const raw = res?.veri?.kategoriler || res?.veri || [];
        const arr = Array.isArray(raw) ? raw : [];
        if (arr.length === 0) {
          setConfirmedLeaf((prev) => new Set(prev).add(catIdStr));
          setLeafLoading((p) => new Set(p).add(catIdStr));
          try {
            let page = 1;
            const merged = [];
            let totalPages = 1;
            do {
              // eslint-disable-next-line no-await-in-loop
              const pr = await fetchProducts({
                icerikTipi: 'urun',
                aktif: aktifFilter,
                kategoriId: catIdStr,
                sayfa: page,
                limit: 100,
              });
              const veri = pr?.veri || {};
              const chunk = veri.icerikler || veri.urunler || [];
              if (Array.isArray(chunk)) merged.push(...chunk);
              totalPages = Number(veri.toplamSayfa) || 1;
              page += 1;
            } while (page <= totalPages && page <= 50);
            setLeafProductsByCat((prev) => ({ ...prev, [catIdStr]: merged }));
          } finally {
            setLeafLoading((p) => {
              const x = new Set(p);
              x.delete(catIdStr);
              return x;
            });
          }
        } else {
          setCategories((prev) => mergeCategoriesDedup(prev, arr));
        }
      } catch {
        setFetchedChildren((prev) => {
          const x = new Set(prev);
          x.delete(catIdStr);
          return x;
        });
      }
    } else if (confirmedLeaf.has(catIdStr) && !leafProductsByCat[catIdStr]?.length) {
      setLeafLoading((p) => new Set(p).add(catIdStr));
      try {
        let page = 1;
        const merged = [];
        let totalPages = 1;
        do {
          // eslint-disable-next-line no-await-in-loop
          const pr = await fetchProducts({
            icerikTipi: 'urun',
            aktif: aktifFilter,
            kategoriId: catIdStr,
            sayfa: page,
            limit: 100,
          });
          const veri = pr?.veri || {};
          const chunk = veri.icerikler || veri.urunler || [];
          if (Array.isArray(chunk)) merged.push(...chunk);
          totalPages = Number(veri.toplamSayfa) || 1;
          page += 1;
        } while (page <= totalPages && page <= 50);
        setLeafProductsByCat((prev) => ({ ...prev, [catIdStr]: merged }));
      } finally {
        setLeafLoading((p) => {
          const x = new Set(p);
          x.delete(catIdStr);
          return x;
        });
      }
    }
  };

  const renderCatNodes = (nodes, depth = 0) => {
    if (!nodes?.length) return null;
    const nestClass =
      depth > 0
        ? 'urun-coklu-secim-modal__nest urun-coklu-secim-modal__nest--deep'
        : 'urun-coklu-secim-modal__nest';

    return nodes.map((category) => {
      const categoryId = category.id || category._id;
      const categoryIdStr = String(categoryId);
      const hasChildrenInTree = category.children && category.children.length > 0;
      const isLeafConfirmed = confirmedLeaf.has(categoryIdStr);
      const treatBranch = hasChildrenInTree || !isLeafConfirmed;
      const isExpanded = expandedIds.has(categoryIdStr);
      const rowPadLeft = 4 + depth * 14;

      const isCatBulkLoading = catBulkLoading.has(categoryIdStr);

      if (!treatBranch) {
        const prods = leafProductsByCat[categoryIdStr] || [];
        const loading = leafLoading.has(categoryIdStr);
        return (
          <div key={categoryId} className="urun-coklu-secim-modal__tree-node">
            <div
              className="urun-coklu-secim-modal__cat-row"
              style={{ paddingLeft: rowPadLeft }}
            >
              <button
                type="button"
                className="category-toggle-btn"
                onClick={() => toggleExpand(categoryIdStr, false, true)}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Daralt' : 'Genişlet'}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
              </button>
              <span className="urun-coklu-secim-modal__cat-label">{category.kategoriAdi}</span>
              <button
                type="button"
                className="urun-coklu-secim-modal__cat-add-btn"
                title="Kategorideki tüm ürünleri ekle"
                onClick={() => addCategoryProducts(categoryIdStr)}
                disabled={isCatBulkLoading || saving}
              >
                {isCatBulkLoading
                  ? <i className="fas fa-spinner fa-spin" aria-hidden />
                  : <i className="fas fa-plus" aria-hidden />}
              </button>
            </div>
            {isExpanded && (
              <div className={nestClass}>
                {loading ? (
                  <div className="urun-coklu-secim-modal__loading">
                    <i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…
                  </div>
                ) : prods.length === 0 ? (
                  <p className="urun-coklu-secim-modal__empty">Bu kategoride ürün yok.</p>
                ) : (
                  <ul className="urun-coklu-secim-modal__product-list">
                    {prods.map((p) => {
                      const pid = String(p.id || p._id);
                      const added = selectedMap.has(pid);
                      return (
                        <li key={pid} className={`urun-coklu-secim-modal__product-row ${added ? 'urun-coklu-secim-modal__product-row--selected' : ''}`}>
                          <div className="urun-coklu-secim-modal__product-main">
                            <span className="urun-coklu-secim-modal__product-code" title={getCode(p)}>
                              {getCode(p)}
                            </span>
                            <span className="urun-coklu-secim-modal__product-name" title={getName(p)}>
                              {getName(p)}
                            </span>
                          </div>
                          <button
                            type="button"
                            className={`urun-coklu-secim-modal__toggle-btn ${added ? 'urun-coklu-secim-modal__toggle-btn--active' : ''}`}
                            title={added ? 'Listeden çıkar' : 'Listeye ekle'}
                            onClick={() => toggleOne(p)}
                            disabled={saving}
                          >
                            <i className={added ? 'fas fa-check' : 'fas fa-plus'} aria-hidden />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      }

      return (
        <div key={categoryId} className="urun-coklu-secim-modal__tree-node">
          <div
            className="urun-coklu-secim-modal__cat-row"
            style={{ paddingLeft: rowPadLeft }}
          >
            <button
              type="button"
              className="category-toggle-btn"
              onClick={() => toggleExpand(categoryIdStr, hasChildrenInTree, isLeafConfirmed)}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Daralt' : 'Genişlet'}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </button>
            <span className="urun-coklu-secim-modal__cat-label">{category.kategoriAdi}</span>
            <button
              type="button"
              className="urun-coklu-secim-modal__cat-add-btn"
              title="Kategorideki tüm ürünleri ekle"
              onClick={() => addCategoryProducts(categoryIdStr)}
              disabled={isCatBulkLoading || saving}
            >
              {isCatBulkLoading
                ? <i className="fas fa-spinner fa-spin" aria-hidden />
                : <i className="fas fa-plus" aria-hidden />}
            </button>
          </div>
          {isExpanded && (
            <div className={nestClass}>{renderCatNodes(category.children, depth + 1)}</div>
          )}
        </div>
      );
    });
  };

  if (!isOpen) return null;

  const selectedRows = Array.from(selectedMap.values());
  const showStep1 = instantApply || step === 1;

  const selectionBody = (
    <>
      <div className="search-box content-management-search-box-full" style={{ marginBottom: 12 }}>
        <i className="fas fa-search" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value.slice(0, 100))}
          placeholder="Ürün adı, kodu, mal/model (en az 3 karakter)"
          autoComplete="off"
        />
        {searchLoading && <i className="fas fa-spinner fa-spin" aria-hidden />}
      </div>
      {debouncedQ.length > 0 && debouncedQ.length < 3 && (
        <p className="content-management-search-hint">Aramak için en az 3 karakter girin.</p>
      )}
      {debouncedQ.length >= 3 && (
        <div className="urun-coklu-secim-modal__search-wrap table-container">
          {searchHits.length === 0 && !searchLoading ? (
            <p className="content-accordion-hint" style={{ padding: 16 }}>
              Sonuç yok.
            </p>
          ) : (
            <table className="admins-table urun-coklu-secim-modal__search-table">
              <thead>
                <tr>
                  <th className="urun-coklu-secim-modal__col-toggle" />
                  <th>Ürün Adı</th>
                  <th>Ürün Kodu</th>
                </tr>
              </thead>
              <tbody>
                {searchHits.map((u) => {
                  const uid = String(u.id || u._id);
                  const sel = selectedMap.has(uid);
                  return (
                    <tr key={uid} className={sel ? 'urun-coklu-secim-modal__row--selected' : ''}>
                      <td className="urun-coklu-secim-modal__col-toggle">
                        <button
                          type="button"
                          className={`urun-coklu-secim-modal__toggle-btn ${sel ? 'urun-coklu-secim-modal__toggle-btn--active' : ''}`}
                          title={sel ? 'Listeden çıkar' : 'Listeye ekle'}
                          onClick={() => toggleOne(u)}
                          disabled={saving}
                        >
                          <i className={sel ? 'fas fa-check' : 'fas fa-plus'} aria-hidden />
                        </button>
                      </td>
                      <td>{getName(u)}</td>
                      <td>{getCode(u)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      <h3 className="urun-coklu-secim-modal__section-title">
        <i className="fas fa-sitemap" aria-hidden />
        Kategorilere göre
      </h3>
      <div className="urun-coklu-secim-modal__tree-panel">{renderCatNodes(buildTree)}</div>
      <div className="urun-coklu-secim-modal__selected-block">
        <div className="urun-coklu-secim-modal__selected-heading">
          Seçilen ürünler
          {saving ? (
            <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 'normal', color: 'var(--text-gray)' }}>
              <i className="fas fa-spinner fa-spin" aria-hidden /> Kaydediliyor…
            </span>
          ) : null}
        </div>
        {selectedRows.length === 0 ? (
          <p className="content-accordion-hint">Henüz seçim yok.</p>
        ) : (
          <div className="urun-coklu-secim-modal__chips">
            {selectedRows.map((r) => (
              <span key={r.id} className="urun-coklu-secim-modal__chip">
                <span className="urun-coklu-secim-modal__chip-text">
                  <strong>{r.kod}</strong> {r.ad}
                </span>
                <button
                  type="button"
                  className="urun-coklu-secim-modal__chip-x"
                  onClick={() => removeOne(r.id)}
                  aria-label="Kaldır"
                  disabled={saving}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="urun-secim-title">
      <div className="modal-content urun-coklu-secim-modal" style={{ maxWidth: 780, width: '94vw' }}>
        <div className="modal-header">
          <h2 id="urun-secim-title">{showStep1 ? stepTitle : 'Seçilen ürünler'}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body">
          {showStep1 ? (
            selectionBody
          ) : (
            <ul className="urun-coklu-secim-modal__confirm-list">
              {selectedRows.map((r) => (
                <li key={r.id}>
                  <strong>{r.kod}</strong>
                  <span>{r.ad}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {instantApply ? (
            <button type="button" className="primary-btn" onClick={onClose}>
              Kapat
            </button>
          ) : (
            <>
              {step === 2 ? (
                <button type="button" className="secondary-btn" onClick={() => setStep(1)}>
                  Geri
                </button>
              ) : null}
              <button type="button" className="secondary-btn" onClick={onClose}>
                İptal
              </button>
              {step === 1 ? (
                <button
                  type="button"
                  className="primary-btn"
                  disabled={selectedRows.length === 0}
                  onClick={() => setStep(2)}
                >
                  İleri
                </button>
              ) : (
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => {
                    onConfirm?.(selectedRows);
                    onClose();
                  }}
                >
                  Tamam
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UrunCokluSecimModal;
